import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { profileService, ProfileUpdateData } from '../services/profileService'
import { paymentService } from '../services/paymentService'
import { stripePlans, formatPrice } from '../lib/stripe'
import { isPasswordValid } from '../utils/passwordStrength'
import PasswordStrengthBar from '../components/PasswordStrengthBar'
import { supabase } from '../lib/supabase'
import {
  UserIcon,
  ArrowLeftIcon,
  CameraIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  BellIcon,
  GlobeAltIcon,
  TrashIcon,
  PlusIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

const ProfilePage = () => {
  const { user, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'personal')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Personal Info State
  const [personalInfo, setPersonalInfo] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    website: user?.website || '',
    timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  })

  // Security State
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  // Business Info State
  const [businessInfo, setBusinessInfo] = useState({
    businessName: user?.businessName || '',
    businessNames: user?.businessNames || ['']
  })

  // Notification Preferences State
  const [notifications, setNotifications] = useState({
    email: user?.notificationPreferences?.email ?? true,
    sms: user?.notificationPreferences?.sms ?? false,
    push: user?.notificationPreferences?.push ?? true,
    marketing: user?.notificationPreferences?.marketing ?? false
  })

  // Social Links State
  const [socialLinks, setSocialLinks] = useState({
    instagram: user?.socialLinks?.instagram || '',
    facebook: user?.socialLinks?.facebook || '',
    twitter: user?.socialLinks?.twitter || '',
    linkedin: user?.socialLinks?.linkedin || ''
  })

  // Profile Picture State
  const [profilePicture, setProfilePicture] = useState<File | null>(null)
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(user?.profilePicture || null)

  // Billing State
  const [billingData, setBillingData] = useState<any>(null)
  const [billingHistory, setBillingHistory] = useState<any[]>([])
  const [billingLoading, setBillingLoading] = useState(false)

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: UserIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'business', label: 'Business', icon: BuildingOfficeIcon },
    { id: 'billing', label: 'Billing', icon: CreditCardIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'social', label: 'Social Links', icon: GlobeAltIcon }
  ]

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
  ]

  useEffect(() => {
    if (searchParams.get('tab')) {
      setActiveTab(searchParams.get('tab') || 'personal')
    }
  }, [searchParams])

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setProfilePicture(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setProfilePicturePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUploadProfilePicture = async () => {
    if (!profilePicture || !user) return

    setLoading(true)
    try {
      console.log('Starting profile picture upload for user:', user.id)
      console.log('File details:', { name: profilePicture.name, size: profilePicture.size, type: profilePicture.type })

      // Use the same logic as the successful test
      const fileExt = profilePicture.name.split('.').pop()
      const fileName = `${user.id}/profile.${fileExt}`
      
      console.log('Upload filename:', fileName)

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase!.storage
        .from('profile-pictures')
        .upload(fileName, profilePicture, {
          upsert: true // Replace existing file
        })

      console.log('Upload result:', { uploadData, uploadError })

      if (uploadError) {
        console.error('Upload error details:', uploadError)
        showMessage('error', `Upload failed: ${uploadError.message}`)
        return
      }

      // Get the public URL with cache busting
      console.log('Getting public URL...')
      const { data: urlData } = supabase!.storage
        .from('profile-pictures')
        .getPublicUrl(fileName)

      console.log('URL data:', urlData)

      // Add cache busting parameter to ensure immediate updates
      const profilePictureUrl = `${urlData.publicUrl}?t=${Date.now()}`
      console.log('Final profile picture URL:', profilePictureUrl)

      // Update the profile with the new picture URL
      console.log('Updating profile with new picture URL...')
      const { data: updateData, error: updateError } = await supabase!
        .from('profiles')
        .update({ profile_picture: profilePictureUrl })
        .eq('id', user.id)
        .select()

      console.log('Profile update result:', { updateData, updateError })

      if (updateError) {
        console.error('Profile update error details:', updateError)
        showMessage('error', `Database update failed: ${updateError.message}`)
        return
      }

      console.log('Profile picture upload completed successfully')
      showMessage('success', 'Profile picture updated successfully!')
      setProfilePicture(null)
      
      // Update the preview immediately with the new URL
      setProfilePicturePreview(profilePictureUrl)
      console.log('Updated preview URL:', profilePictureUrl)
      
      // Refresh user data to show updated profile picture
      console.log('Refreshing user data...')
      await refreshUser()
      console.log('User data refreshed')
      
    } catch (error) {
      console.error('Upload error:', error)
      showMessage('error', 'Failed to upload profile picture')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteProfilePicture = async () => {
    if (!user) return

    setLoading(true)
    try {
      const result = await profileService.deleteProfilePicture(user.id)
      if (result.success) {
        setProfilePicturePreview(null)
        showMessage('success', 'Profile picture removed successfully!')
        await refreshUser() // Refresh user data to show removed profile picture
      } else {
        showMessage('error', result.error || 'Failed to remove profile picture')
      }
    } catch (error) {
      showMessage('error', 'Failed to remove profile picture')
    } finally {
      setLoading(false)
    }
  }

  const handlePersonalInfoSave = async () => {
    if (!user) return

    setLoading(true)
    try {
      const updateData: ProfileUpdateData = {
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        phone: personalInfo.phone,
        bio: personalInfo.bio,
        website: personalInfo.website,
        timezone: personalInfo.timezone
      }

      const result = await profileService.updateProfile(user.id, updateData)
      if (result.success) {
        showMessage('success', 'Personal information updated successfully!')
        await refreshUser() // Refresh user data to show updated information
      } else {
        showMessage('error', result.error || 'Failed to update personal information')
      }
    } catch (error) {
      showMessage('error', 'Failed to update personal information')
    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = async () => {
    if (!user || personalInfo.email === user.email) return

    setLoading(true)
    try {
      const result = await profileService.changeEmail(personalInfo.email)
      if (result.success) {
        showMessage('success', 'Email change confirmation sent! Check your new email.')
      } else {
        showMessage('error', result.error || 'Failed to change email')
      }
    } catch (error) {
      showMessage('error', 'Failed to change email')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    if (!securityData.newPassword || !securityData.confirmPassword) {
      showMessage('error', 'Please fill in all password fields')
      return
    }

    if (securityData.newPassword !== securityData.confirmPassword) {
      showMessage('error', 'New passwords do not match')
      return
    }

    if (!isPasswordValid(securityData.newPassword)) {
      showMessage('error', 'Password does not meet security requirements')
      return
    }

    setLoading(true)
    try {
      const result = await profileService.changePassword({
        currentPassword: securityData.currentPassword,
        newPassword: securityData.newPassword
      })

      if (result.success) {
        setSecurityData({ currentPassword: '', newPassword: '', confirmPassword: '' })
        showMessage('success', 'Password changed successfully!')
      } else {
        showMessage('error', result.error || 'Failed to change password')
      }
    } catch (error) {
      showMessage('error', 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  const handleBusinessInfoSave = async () => {
    if (!user) return

    setLoading(true)
    try {
      const filteredBusinessNames = businessInfo.businessNames.filter(name => name.trim() !== '')
      
      const updateData: ProfileUpdateData = {
        businessName: businessInfo.businessName,
        businessNames: filteredBusinessNames
      }

      const result = await profileService.updateProfile(user.id, updateData)
      if (result.success) {
        showMessage('success', 'Business information updated successfully!')
        await refreshUser() // Refresh user data to show updated business information
      } else {
        showMessage('error', result.error || 'Failed to update business information')
      }
    } catch (error) {
      showMessage('error', 'Failed to update business information')
    } finally {
      setLoading(false)
    }
  }

  const handleNotificationsSave = async () => {
    if (!user) return

    setLoading(true)
    try {
      const updateData: ProfileUpdateData = {
        notificationPreferences: notifications
      }

      const result = await profileService.updateProfile(user.id, updateData)
      if (result.success) {
        showMessage('success', 'Notification preferences updated successfully!')
        await refreshUser() // Refresh user data to show updated notification preferences
      } else {
        showMessage('error', result.error || 'Failed to update notification preferences')
      }
    } catch (error) {
      showMessage('error', 'Failed to update notification preferences')
    } finally {
      setLoading(false)
    }
  }

  const handleSocialLinksSave = async () => {
    if (!user) return

    setLoading(true)
    try {
      const updateData: ProfileUpdateData = {
        socialLinks: socialLinks
      }

      const result = await profileService.updateProfile(user.id, updateData)
      if (result.success) {
        showMessage('success', 'Social links updated successfully!')
        await refreshUser() // Refresh user data to show updated social links
      } else {
        showMessage('error', result.error || 'Failed to update social links')
      }
    } catch (error) {
      showMessage('error', 'Failed to update social links')
    } finally {
      setLoading(false)
    }
  }

  const addBusinessName = () => {
    setBusinessInfo(prev => ({
      ...prev,
      businessNames: [...prev.businessNames, '']
    }))
  }

  const removeBusinessName = (index: number) => {
    setBusinessInfo(prev => ({
      ...prev,
      businessNames: prev.businessNames.filter((_, i) => i !== index)
    }))
  }

  const updateBusinessName = (index: number, value: string) => {
    setBusinessInfo(prev => ({
      ...prev,
      businessNames: prev.businessNames.map((name, i) => i === index ? value : name)
    }))
  }

  // Billing Functions
  const loadBillingData = async () => {
    if (!user) return

    setBillingLoading(true)
    try {
      const [subscriptionResult, historyResult] = await Promise.all([
        paymentService.getSubscriptionStatus(user.id),
        paymentService.getBillingHistory(user.id)
      ])

      setBillingData(subscriptionResult)
      if (historyResult.success) {
        setBillingHistory(historyResult.invoices || [])
      }
    } catch (error) {
      console.error('Error loading billing data:', error)
      showMessage('error', 'Failed to load billing information')
    } finally {
      setBillingLoading(false)
    }
  }

  const handleCancelSubscription = async () => {
    if (!user || !billingData?.isActive) return

    setBillingLoading(true)
    try {
      const result = await paymentService.cancelSubscription(user.id)
      if (result.success) {
        showMessage('success', 'Subscription cancelled successfully')
        await refreshUser()
        await loadBillingData()
      } else {
        showMessage('error', result.error || 'Failed to cancel subscription')
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error)
      showMessage('error', 'Failed to cancel subscription')
    } finally {
      setBillingLoading(false)
    }
  }

  // Load billing data when billing tab is accessed
  useEffect(() => {
    if (activeTab === 'billing' && user) {
      loadBillingData()
    }
  }, [activeTab, user])

  const renderPersonalInfo = () => (
    <div className="space-y-6">
      {/* Profile Picture Section */}
      <div className="glass-mockup rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Profile Picture</h3>
        <div className="flex items-center space-x-6">
          <div className="relative">
            {profilePicturePreview ? (
              <img
                src={profilePicturePreview}
                alt="Profile"
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
            )}
            <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-600 transition-colors">
              <CameraIcon className="w-4 h-4 text-white" />
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
            </label>
          </div>
          <div className="flex-1">
            <p className="text-gray-300 text-sm mb-3">
              Upload a new profile picture. Recommended size: 400x400px
            </p>
            <div className="flex space-x-3">
              {profilePicture && (
                <button
                  onClick={handleUploadProfilePicture}
                  disabled={loading}
                  className="btn-gradient px-4 py-2 text-sm"
                >
                  Upload
                </button>
              )}
              {profilePicturePreview && (
                <button
                  onClick={handleDeleteProfilePicture}
                  disabled={loading}
                  className="btn-secondary px-4 py-2 text-sm"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="glass-mockup rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              First Name
            </label>
            <input
              type="text"
              value={personalInfo.firstName}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, firstName: e.target.value }))}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={personalInfo.lastName}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, lastName: e.target.value }))}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={personalInfo.phone}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, phone: e.target.value }))}
              className="input-field"
              placeholder="+1 (555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Timezone
            </label>
            <select
              value={personalInfo.timezone}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, timezone: e.target.value }))}
              className="input-field"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Website
            </label>
            <input
              type="url"
              value={personalInfo.website}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, website: e.target.value }))}
              className="input-field"
              placeholder="https://yourwebsite.com"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Bio
          </label>
          <textarea
            value={personalInfo.bio}
            onChange={(e) => setPersonalInfo(prev => ({ ...prev, bio: e.target.value }))}
            className="input-field h-24 resize-none"
            placeholder="Tell us about yourself and your photography business..."
          />
        </div>
        <div className="mt-6">
          <button
            onClick={handlePersonalInfoSave}
            disabled={loading}
            className="btn-gradient"
          >
            Save Personal Information
          </button>
        </div>
      </div>
    </div>
  )

  const renderSecurity = () => (
    <div className="space-y-6">
      {/* Email Change */}
      <div className="glass-mockup rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Email Address</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={personalInfo.email}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, email: e.target.value }))}
              className="input-field"
            />
          </div>
          {personalInfo.email !== user?.email && (
            <button
              onClick={handleEmailChange}
              disabled={loading}
              className="btn-gradient"
            >
              Change Email
            </button>
          )}
        </div>
      </div>

      {/* Password Change */}
      <div className="glass-mockup rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={securityData.currentPassword}
                onChange={(e) => setSecurityData(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="input-field pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPasswords.current ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={securityData.newPassword}
                onChange={(e) => setSecurityData(prev => ({ ...prev, newPassword: e.target.value }))}
                className="input-field pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPasswords.new ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
            {securityData.newPassword && (
              <div className="mt-2">
                <PasswordStrengthBar password={securityData.newPassword} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={securityData.confirmPassword}
                onChange={(e) => setSecurityData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="input-field pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPasswords.confirm ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <button
            onClick={handlePasswordChange}
            disabled={loading || !securityData.newPassword || !securityData.confirmPassword}
            className="btn-gradient"
          >
            Change Password
          </button>
        </div>
      </div>
    </div>
  )

  const renderBusiness = () => (
    <div className="space-y-6">
      <div className="glass-mockup rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Business Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Primary Business Name
            </label>
            <input
              type="text"
              value={businessInfo.businessName}
              onChange={(e) => setBusinessInfo(prev => ({ ...prev, businessName: e.target.value }))}
              className="input-field"
              placeholder="Your Photography Studio"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Additional Business Names
            </label>
            <p className="text-gray-400 text-sm mb-3">
              Add other business names or brands you operate under
            </p>
            {businessInfo.businessNames.map((name, index) => (
              <div key={index} className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => updateBusinessName(index, e.target.value)}
                  className="input-field flex-1"
                  placeholder="Business name"
                />
                {businessInfo.businessNames.length > 1 && (
                  <button
                    onClick={() => removeBusinessName(index)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addBusinessName}
              className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add Business Name</span>
            </button>
          </div>
          
          <button
            onClick={handleBusinessInfoSave}
            disabled={loading}
            className="btn-gradient"
          >
            Save Business Information
          </button>
        </div>
      </div>
    </div>
  )

  const renderNotifications = () => (
    <div className="space-y-6">
      <div className="glass-mockup rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Email Notifications</h4>
              <p className="text-gray-400 text-sm">Receive notifications via email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.email}
                onChange={(e) => setNotifications(prev => ({ ...prev, email: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">SMS Notifications</h4>
              <p className="text-gray-400 text-sm">Receive notifications via text message</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.sms}
                onChange={(e) => setNotifications(prev => ({ ...prev, sms: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Push Notifications</h4>
              <p className="text-gray-400 text-sm">Receive browser push notifications</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.push}
                onChange={(e) => setNotifications(prev => ({ ...prev, push: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Marketing Communications</h4>
              <p className="text-gray-400 text-sm">Receive updates about new features and tips</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.marketing}
                onChange={(e) => setNotifications(prev => ({ ...prev, marketing: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          <button
            onClick={handleNotificationsSave}
            disabled={loading}
            className="btn-gradient"
          >
            Save Notification Preferences
          </button>
        </div>
      </div>
    </div>
  )

  const renderSocialLinks = () => (
    <div className="space-y-6">
      <div className="glass-mockup rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Social Media Links</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Instagram
            </label>
            <input
              type="url"
              value={socialLinks.instagram}
              onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
              className="input-field"
              placeholder="https://instagram.com/yourusername"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Facebook
            </label>
            <input
              type="url"
              value={socialLinks.facebook}
              onChange={(e) => setSocialLinks(prev => ({ ...prev, facebook: e.target.value }))}
              className="input-field"
              placeholder="https://facebook.com/yourpage"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Twitter
            </label>
            <input
              type="url"
              value={socialLinks.twitter}
              onChange={(e) => setSocialLinks(prev => ({ ...prev, twitter: e.target.value }))}
              className="input-field"
              placeholder="https://twitter.com/yourusername"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              LinkedIn
            </label>
            <input
              type="url"
              value={socialLinks.linkedin}
              onChange={(e) => setSocialLinks(prev => ({ ...prev, linkedin: e.target.value }))}
              className="input-field"
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>
          
          <button
            onClick={handleSocialLinksSave}
            disabled={loading}
            className="btn-gradient"
          >
            Save Social Links
          </button>
        </div>
      </div>
    </div>
  )

  const renderBilling = () => {
    if (billingLoading) {
      return (
        <div className="glass-mockup rounded-2xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white">Loading billing information...</p>
        </div>
      )
    }

    const currentPlan = stripePlans[user?.subscriptionStatus as keyof typeof stripePlans] || stripePlans.freemium

    return (
      <div className="space-y-6">
        {/* Current Subscription */}
        <div className="glass-mockup rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Current Subscription</h3>
          
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                currentPlan.id === 'freemium' 
                  ? 'bg-gray-500/20' 
                  : currentPlan.id === 'professional'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600'
                  : 'bg-gradient-to-r from-purple-500 to-pink-600'
              }`}>
                {currentPlan.id === 'freemium' ? (
                  <UserIcon className="w-6 h-6 text-gray-400" />
                ) : (
                  <CheckCircleIcon className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h4 className="text-white font-semibold">{currentPlan.name} Plan</h4>
                <p className="text-gray-400 text-sm">
                  {currentPlan.id === 'freemium' 
                    ? 'Free forever' 
                    : `${formatPrice(currentPlan.price)}/month`
                  }
                </p>
              </div>
            </div>
            
            {billingData?.isActive && (
              <div className="text-right">
                <div className="flex items-center space-x-2 text-emerald-400 mb-1">
                  <CheckCircleIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">Active</span>
                </div>
                {billingData.currentPeriodEnd && (
                  <p className="text-gray-400 text-xs">
                    Next billing: {new Date(billingData.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </div>

          {currentPlan.id === 'freemium' ? (
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-center space-x-3 mb-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400 font-medium">Upgrade to unlock premium features</span>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Get unlimited projects, AI automation, advanced analytics, and more.
              </p>
              <button
                onClick={() => navigate('/upgrade')}
                className="btn-gradient"
              >
                View Plans & Upgrade
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-emerald-400 font-medium mb-2">Premium Features Active</p>
                <p className="text-gray-300 text-sm">
                  You have access to all premium features including unlimited projects, 
                  AI automation, and priority support.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/upgrade')}
                  className="btn-secondary"
                >
                  Change Plan
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={billingLoading}
                  className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl transition-colors border border-red-500/30"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Billing History */}
        {billingHistory.length > 0 && (
          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Billing History</h3>
            
            <div className="space-y-3">
              {billingHistory.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <CreditCardIcon className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{invoice.planName} Plan</p>
                      <p className="text-gray-400 text-sm">
                        {new Date(invoice.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-white font-semibold">{formatPrice(invoice.amount)}</p>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        invoice.status === 'paid' ? 'bg-emerald-400' : 'bg-red-400'
                      }`}></div>
                      <span className={`text-xs font-medium ${
                        invoice.status === 'paid' ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Method */}
        <div className="glass-mockup rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Payment Method</h3>
          
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center space-x-3">
              <CreditCardIcon className="w-6 h-6 text-gray-400" />
              <div>
                <p className="text-gray-300">No payment method on file</p>
                <p className="text-gray-400 text-sm">Add a payment method to manage your subscription</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/checkout')}
            className="btn-secondary mt-4"
          >
            Add Payment Method
          </button>
        </div>
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return renderPersonalInfo()
      case 'security':
        return renderSecurity()
      case 'business':
        return renderBusiness()
      case 'billing':
        return renderBilling()
      case 'notifications':
        return renderNotifications()
      case 'social':
        return renderSocialLinks()
      default:
        return renderPersonalInfo()
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
              <span className="text-lg font-medium">Back to Dashboard</span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Profile <span className="text-gray-400">Settings</span>
              </span>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white font-medium`}
        >
          {message.text}
        </motion.div>
      )}

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass-mockup rounded-2xl p-6">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-colors text-left ${
                      activeTab === tab.id
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderTabContent()}
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default ProfilePage 